import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';

import { Store } from '../../../../shared/store';
import { FavoriteState } from './favorite.state';
import { Playlist } from '@kinomap-dev/ng-ui-lib/lib/models/playlist.model';
import { FavoriteApi } from '../../api/favorite.api';
import { FavoriteService } from '../../services/favorite.service';
import { Sport } from '../../models/sport';
import { timeDelay } from '../../helpers/time.helpers';
import { BrowseTypesEnum } from '../../models/browse-types.enum';
import {
  GpsOnly,
  VideoSection,
  Workout,
  Coaching,
} from '@kinomap-dev/ng-ui-lib';

const favoriteState: FavoriteState = new FavoriteState();

@Injectable({ providedIn: 'root' })
export class FavoriteStore extends Store<FavoriteState> {
  videos$ = this.state$.pipe(map((s: FavoriteState) => s.videos));
  isLoadingVideos$ = this.state$.pipe(
    map((s: FavoriteState) => s.isLoadingVideos)
  );
  areVideosComplete$ = this.state$.pipe(
    map((s: FavoriteState) => s.areVideosComplete)
  );

  gpsTracks$ = this.state$.pipe(map((s: FavoriteState) => s.gpsTracks));
  isLoadingGpsTracks$ = this.state$.pipe(
    map((s: FavoriteState) => s.isLoadingGpsTracks)
  );
  areGpsTracksComplete$ = this.state$.pipe(
    map((s: FavoriteState) => s.areGpsTracksComplete)
  );

  workouts$ = this.state$.pipe(map((s: FavoriteState) => s.workouts));
  isLoadingWorkouts$ = this.state$.pipe(
    map((s: FavoriteState) => s.isLoadingWorkouts)
  );
  areWorkoutsComplete$ = this.state$.pipe(
    map((s: FavoriteState) => s.areWorkoutsComplete)
  );

  coachings$ = this.state$.pipe(map((s: FavoriteState) => s.coachings));
  isLoadingCoachings$ = this.state$.pipe(
    map((s: FavoriteState) => s.isLoadingCoachings)
  );
  areCoachingsComplete$ = this.state$.pipe(
    map((s: FavoriteState) => s.areCoachingsComplete)
  );

  playlists$ = this.state$.pipe(map((s: FavoriteState) => s.playlists));
  isLoadingPlaylists$ = this.state$.pipe(
    map((s: FavoriteState) => s.isLoadingPlaylists)
  );
  arePlaylistsComplete$ = this.state$.pipe(
    map((s: FavoriteState) => s.arePlaylistsComplete)
  );

  constructor(
    private favoriteApi: FavoriteApi,
    private favoriteService: FavoriteService
  ) {
    super(favoriteState);
  }

  setVideos(nextVideos: VideoSection[]): void {
    this.setState({ ...this.state, videos: nextVideos });
  }
  setIsLoadingVideos(nextIsLoadingVideos: boolean): void {
    this.setState({ ...this.state, isLoadingVideos: nextIsLoadingVideos });
  }
  setAreVideosComplete(nextAreVideosComplete: boolean): void {
    this.setState({ ...this.state, areVideosComplete: nextAreVideosComplete });
  }

  setGpsTracks(nextGpsTracks: GpsOnly[]): void {
    this.setState({ ...this.state, gpsTracks: nextGpsTracks });
  }
  setIsLoadingGpsTracks(nextIsLoadingGpsTracks: boolean): void {
    this.setState({
      ...this.state,
      isLoadingGpsTracks: nextIsLoadingGpsTracks,
    });
  }
  setAreGpsTracksComplete(nextAreGpsTracksComplete: boolean): void {
    this.setState({
      ...this.state,
      areGpsTracksComplete: nextAreGpsTracksComplete,
    });
  }

  setWorkouts(nextWorkouts: Workout[]): void {
    this.setState({ ...this.state, workouts: nextWorkouts });
  }
  setIsLoadingWorkouts(nextIsLoadingWorkouts: boolean): void {
    this.setState({
      ...this.state,
      isLoadingWorkouts: nextIsLoadingWorkouts,
    });
  }
  setAreWorkoutsComplete(nextAreWorkoutsComplete: boolean): void {
    this.setState({
      ...this.state,
      areWorkoutsComplete: nextAreWorkoutsComplete,
    });
  }

  setCoachings(nextCoachings: Coaching[]): void {
    this.setState({ ...this.state, coachings: nextCoachings });
  }
  setIsLoadingCoachings(nextIsLoadingCoachings: boolean): void {
    this.setState({
      ...this.state,
      isLoadingCoachings: nextIsLoadingCoachings,
    });
  }
  setAreCoachingsComplete(nextAreCoachingsComplete: boolean): void {
    this.setState({
      ...this.state,
      areCoachingsComplete: nextAreCoachingsComplete,
    });
  }

  setPlaylists(nextPlaylists: Playlist[]): void {
    this.setState({ ...this.state, playlists: nextPlaylists });
  }
  setIsLoadingPlaylists(nextIsLoadingPlaylists: boolean): void {
    this.setState({
      ...this.state,
      isLoadingPlaylists: nextIsLoadingPlaylists,
    });
  }
  setArePlaylistsComplete(nextArePlaylistsComplete: boolean): void {
    this.setState({
      ...this.state,
      arePlaylistsComplete: nextArePlaylistsComplete,
    });
  }

  async getFavorites({
    newSelectedSport,
    nextPage,
    contentType,
  }: {
    newSelectedSport?: Sport;
    nextPage: boolean;
    contentType: BrowseTypesEnum | string;
  }): Promise<void> {
    const {
      getIsLastPageLoaded,
      getAreFavoriteComplete,
      getIsLoading,
      setAreFavoriteComplete,
      setIsLoading,
      setFavorites,
    } = this.defineVariablesWithContentType(contentType);

    if (nextPage && getAreFavoriteComplete()) {
      return;
    }

    let goOn: boolean = !getIsLoading();
    while (!goOn) {
      await timeDelay(100);
      goOn = !getIsLoading();
    }

    if (!!newSelectedSport) {
      setFavorites([]);
    }

    const isNotPostingOrDeleting: boolean = !!newSelectedSport || nextPage;
    if (isNotPostingOrDeleting) {
      setIsLoading(true);
    }

    try {
      const fetchedFavorites:
        | VideoSection[]
        | GpsOnly[]
        | Workout[]
        | Coaching[]
        | Playlist[] = await firstValueFrom(
          this.favoriteApi.getFavorites$(newSelectedSport, nextPage, contentType)
        );

      if (!nextPage && !newSelectedSport) {
        setFavorites([]);
      }

      const newFavorites:
        | VideoSection[]
        | GpsOnly[]
        | Workout[]
        | Coaching[]
        | Playlist[] = this.getFavoritesWithoutDuplicate(
          fetchedFavorites,
          contentType
        );

      setFavorites(newFavorites);
      setAreFavoriteComplete(getIsLastPageLoaded());
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  async postfavorite(
    id: number,
    contentType: BrowseTypesEnum | string
  ): Promise<void> {
    try {
      await firstValueFrom(this.favoriteApi.postFavorite$(id, contentType));
    } catch (err) {
      console.error(err);
      this.favoriteService.addOrRemoveFavorite(contentType, id, false);
    } finally {
      this.getFavorites({
        nextPage: false,
        contentType,
      });
    }
  }

  async deleteFavorite(
    id: number,
    contentType: BrowseTypesEnum | string
  ): Promise<void> {
    try {
      await firstValueFrom(this.favoriteApi.deleteFavorite$(id, contentType));
    } catch (err) {
      console.error(err);
      this.favoriteService.addOrRemoveFavorite(contentType, id);
    } finally {
      this.getFavorites({
        nextPage: false,
        contentType,
      });
    }
  }

  private defineVariablesWithContentType(
    contentType: BrowseTypesEnum | string
  ): {
    getIsLastPageLoaded: () => boolean;
    getAreFavoriteComplete: () => boolean;
    getIsLoading: () => boolean;
    setAreFavoriteComplete: (value: boolean) => void;
    setIsLoading: (value: boolean) => void;
    setFavorites: (
      value: VideoSection[] | GpsOnly[] | Workout[] | Coaching[] | Playlist[]
    ) => void;
  } {
    const obj: {
      [key in BrowseTypesEnum]:
      | {
        getIsLastPageLoaded: () => boolean;
        getAreFavoriteComplete: () => boolean;
        getIsLoading: () => boolean;
        setAreFavoriteComplete: (value: boolean) => void;
        setIsLoading: (value: boolean) => void;
        setFavorites: (
          value:
            | VideoSection[]
            | GpsOnly[]
            | Workout[]
            | Coaching[]
            | Playlist[]
        ) => void;
      }
      | undefined;
    } = {
      video: {
        getIsLastPageLoaded: () => this.favoriteApi.isVideoLastPageLoaded,
        getAreFavoriteComplete: () => this.state.areVideosComplete,
        getIsLoading: () => this.state.isLoadingVideos,
        setAreFavoriteComplete: (value: boolean) => {
          this.setAreVideosComplete(value);
        },
        setIsLoading: (value: boolean) => {
          this.setIsLoadingVideos(value);
        },
        setFavorites: (value: VideoSection[]) => {
          this.setVideos(value);
        },
      },
      'gps-track': {
        getIsLastPageLoaded: () => this.favoriteApi.isGpsLastPageLoaded,
        getAreFavoriteComplete: () => this.state.areGpsTracksComplete,
        getIsLoading: () => this.state.isLoadingGpsTracks,
        setAreFavoriteComplete: (value: boolean) => {
          this.setAreGpsTracksComplete(value);
        },
        setIsLoading: (value: boolean) => {
          this.setIsLoadingGpsTracks(value);
        },
        setFavorites: (value: GpsOnly[]) => {
          this.setGpsTracks(value);
        },
      },
      workout: {
        getIsLastPageLoaded: () => this.favoriteApi.isWorkoutLastPageLoaded,
        getAreFavoriteComplete: () => this.state.areWorkoutsComplete,
        getIsLoading: () => this.state.isLoadingWorkouts,
        setAreFavoriteComplete: (value: boolean) => {
          this.setAreWorkoutsComplete(value);
        },
        setIsLoading: (value: boolean) => {
          this.setIsLoadingWorkouts(value);
        },
        setFavorites: (value: Workout[]) => {
          this.setWorkouts(value);
        },
      },
      coaching: {
        getIsLastPageLoaded: () => this.favoriteApi.isCoachingLastPageLoaded,
        getAreFavoriteComplete: () => this.state.areCoachingsComplete,
        getIsLoading: () => this.state.isLoadingCoachings,
        setAreFavoriteComplete: (value: boolean) => {
          this.setAreCoachingsComplete(value);
        },
        setIsLoading: (value: boolean) => {
          this.setIsLoadingCoachings(value);
        },
        setFavorites: (value: Coaching[]) => {
          this.setCoachings(value);
        },
      },
      playlist: {
        getIsLastPageLoaded: () => this.favoriteApi.isPlaylistLastPageLoaded,
        getAreFavoriteComplete: () => this.state.arePlaylistsComplete,
        getIsLoading: () => this.state.isLoadingPlaylists,
        setAreFavoriteComplete: (value: boolean) => {
          this.setArePlaylistsComplete(value);
        },
        setIsLoading: (value: boolean) => {
          this.setIsLoadingPlaylists(value);
        },
        setFavorites: (value: Playlist[]) => {
          this.setPlaylists(value);
        },
      },
    };

    return obj[contentType];
  }

  private getFavoritesWithoutDuplicate(
    fetchedFavorites:
      | VideoSection[]
      | GpsOnly[]
      | Workout[]
      | Coaching[]
      | Playlist[],
    contentType: BrowseTypesEnum | string
  ): VideoSection[] | GpsOnly[] | Workout[] | Coaching[] | Playlist[] {
    const obj: {
      [key in BrowseTypesEnum]:
      | VideoSection[]
      | GpsOnly[]
      | Workout[]
      | Coaching[]
      | Playlist[];
    } = {
      video: this.state.videos,
      'gps-track': this.state.gpsTracks,
      workout: this.state.workouts,
      coaching: this.state.coachings,
      playlist: this.state.playlists,
    };
    const newFavoritesWithDuplicates: (
      | VideoSection
      | GpsOnly
      | Workout
      | Coaching
      | Playlist
    )[] = [...obj[contentType], ...fetchedFavorites];
    newFavoritesWithDuplicates.forEach(
      (content) => (obj[content.id] = content)
    );
    return [
      ...new Set(newFavoritesWithDuplicates.map((favorite) => favorite.id)),
    ].map((id) => obj[id]);
  }
}
